import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  CircularProgress,
  Alert,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import photoService from '../../services/photoService';

const MAX_IMAGE_SIZE_BYTES = 2 * 1024 * 1024; // 2MB (mesmo limite do Dashboard)
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

/**
 * ReplyEditor Component
 * Rich text editor for admins to reply to support tickets
 * Uses Quill for HTML formatting
 * Suporta imagens inline (botão da toolbar + colar print) via FileService,
 * espelhando o comportamento do Dashboard.
 */
export function ReplyEditor({ ticketId, onSubmit, loading = false, disabled = false }) {
  const [content, setContent] = useState('');
  const [error, setError] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const quillRef = useRef(null);

  const uploadAndInsertImage = useCallback(async (file) => {
    if (!file) return;

    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      setError('Imagem muito grande. Máximo: 2MB.');
      return;
    }
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setError('Tipo de imagem não permitido. Use: JPG, PNG, GIF ou WebP.');
      return;
    }

    try {
      setError('');
      setUploadingImage(true);
      const result = await photoService.uploadTicketImage(file);
      const url = result?.fileUrl;
      if (!url) {
        setError('Erro ao fazer upload da imagem.');
        return;
      }

      const editor = quillRef.current?.getEditor();
      if (editor) {
        const range = editor.getSelection(true);
        editor.insertEmbed(range.index, 'image', url, 'user');
        editor.setSelection(range.index + 1, 0, 'user');
      }
    } catch (err) {
      console.error('Ticket image upload error:', err);
      setError('Erro ao fazer upload da imagem.');
    } finally {
      setUploadingImage(false);
    }
  }, []);

  const uploadRef = useRef(uploadAndInsertImage);
  uploadRef.current = uploadAndInsertImage;

  // modules precisa de identidade estável — react-quill recria o editor se mudar
  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        ['blockquote', 'code-block'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ script: 'sub' }, { script: 'super' }],
        [{ indent: '-1' }, { indent: '+1' }],
        ['link', 'image'],
        ['clean'],
      ],
      handlers: {
        image: () => {
          const input = document.createElement('input');
          input.setAttribute('type', 'file');
          input.setAttribute('accept', ALLOWED_IMAGE_TYPES.join(','));
          input.click();
          input.onchange = () => {
            const file = input.files && input.files[0];
            if (file) uploadRef.current(file);
          };
        },
      },
    },
  }), []);

  const formats = useMemo(() => [
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'blockquote',
    'code-block',
    'list',
    'bullet',
    'indent',
    'script',
    'link',
    'image',
  ], []);

  // Colar print da área de transferência → upload em vez de embutir base64
  useEffect(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return undefined;

    const handlePaste = (e) => {
      const clipboardData = e.clipboardData || window.clipboardData;
      if (!clipboardData || !clipboardData.items) return;

      for (const item of clipboardData.items) {
        if (item.type && item.type.startsWith('image/')) {
          e.preventDefault();
          const file = item.getAsFile();
          if (file) uploadRef.current(file);
          break;
        }
      }
    };

    editor.root.addEventListener('paste', handlePaste);
    return () => editor.root.removeEventListener('paste', handlePaste);
  }, []);

  const handleSubmit = async () => {
    // Trim HTML to check if it's actually empty (imagem conta como conteúdo)
    const trimmedContent = content.replace(/<[^>]*>/g, '').trim();
    const hasImage = /<img\s/i.test(content);

    if (!trimmedContent && !hasImage) {
      setError('Resposta não pode estar vazia');
      return;
    }

    try {
      setError('');
      await onSubmit({
        content: content,
      });
      setContent(''); // Clear editor on success
    } catch (err) {
      setError(err.message || 'Erro ao enviar resposta');
    }
  };

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent>
        <Stack spacing={2}>
          <Box>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
              Sua Resposta
            </label>
            <ReactQuill
              ref={quillRef}
              theme="snow"
              value={content}
              onChange={setContent}
              modules={modules}
              formats={formats}
              readOnly={disabled}
              style={{
                minHeight: '150px',
                backgroundColor: disabled ? '#f5f5f5' : 'white',
              }}
            />
          </Box>

          {error && <Alert severity="error">{error}</Alert>}

          <Stack direction="row" spacing={2} justifyContent="flex-end" alignItems="center">
            {uploadingImage && (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={16} />
                <span style={{ fontSize: '0.85rem', opacity: 0.7 }}>Enviando imagem...</span>
              </Stack>
            )}
            <Button
              variant="contained"
              endIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
              onClick={handleSubmit}
              disabled={loading || disabled || uploadingImage || !content.trim()}
            >
              {loading ? 'Enviando...' : 'Enviar Resposta'}
            </Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default ReplyEditor;
